import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITY } from "../../helpers/permissions";
import { blockDemoSave } from "../../helpers/guestMode";
import {
  createCatalogTask,
  loadTaskCatalog,
  setCatalogTaskActive,
  TaskCatalogDuplicateError,
  updateCatalogTask,
} from "../../helpers/tasks/taskCatalog";
import { isTasksUnavailableError } from "../../helpers/tasks/projectTasks";
import { DESCRIPTION_MAX, TITLE_MAX, validateTaskInput } from "../../helpers/tasks/taskModel";
import { durationToInput, formatDuration, parseDurationInput } from "../../helpers/tasks/taskDuration";
import { DurationInput } from "./TaskParts";
import { useWorkSchedule } from "../../hooks/useWorkSchedule";
import {
  RecBar,
  RecField,
  RecIcon,
  useKeepFocusedFieldVisible,
  useKeyboardOpen,
} from "../materials/MaterialRecordParts";
import { BarMenu } from "../timelogs/TimeLogBarMenu";
import { TimeLogsActionBar, TimeLogsScopeTabs } from "../timelogs/TimeLogsToolbar";
import { formatLogTimestamp } from "../timelogs/timeLogModel";
import { TaskCatalogDesktop } from "./TaskCatalogDesktop";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import "./tasks.css";

function CatalogRow({ item, canEdit, onOpen, onEdit, onToggleActive }) {
  const { minutesPerDay } = useWorkSchedule();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.type === "keydown") {
        if (event.key === "Escape") setOpen(false);
        return;
      }
      if (!ref.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);
  const archived = item.is_active === false;
  return (
    <div className={`tk-catrow${archived ? " is-archived" : ""}`} data-testid="catalog-row">
      <button type="button" className="tk-catrow__main" onClick={() => onOpen(item)} aria-label={`Open ${item.title}`}>
        <span className="tk-catrow__title">{item.title}</span>
        {item.description ? <span className="tk-catrow__desc">{item.description}</span> : null}
        {item.planned_duration_minutes ? (
          <span className="tk-chip tk-chip--duration">{formatDuration(item.planned_duration_minutes, minutesPerDay)}</span>
        ) : null}
        {archived ? <span className="tk-chip tk-chip--status">Archived</span> : null}
      </button>
      {canEdit ? (
        <div className="tk-menuwrap" ref={ref}>
          <button
            type="button"
            className="tk-iconbtn"
            aria-label={`Actions for ${item.title}`}
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <RecIcon name="more" className="tk-more" />
          </button>
          {open ? (
            <div className="mat-menu tk-menu" role="menu">
              <button type="button" role="menuitem" className="mat-menu__item" onClick={() => { setOpen(false); onEdit(item); }}>
                <span>Edit</span>
              </button>
              <button
                type="button"
                role="menuitem"
                className={`mat-menu__item${archived ? "" : " tk-danger"}`}
                onClick={() => { setOpen(false); onToggleActive(item); }}
              >
                <span>{archived ? "Restore" : "Archive"}</span>
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function CatalogDetails({ item, canEdit, onBack, onEdit, onToggleActive }) {
  const { minutesPerDay } = useWorkSchedule();
  const archived = item.is_active === false;
  return (
    <div className="screen nx-screen rec-page rec-page--detail" data-testid="catalog-details">
      <RecBar
        title="Task Details"
        onBack={onBack}
        trailing={
          <BarMenu
            items={
              canEdit
                ? [
                    {
                      key: "toggle",
                      label: archived ? "Restore task" : "Archive task",
                      danger: !archived,
                      onSelect: () => onToggleActive(item),
                    },
                  ]
                : []
            }
          />
        }
      />
      <div className="rec-body">
        <section className="rec-card tk-hero">
          <span className="tk-hero__icon">
            <RecIcon name="task" />
          </span>
          <h2 className="tk-hero__title">{item.title}</h2>
        </section>
        <div className="tk-chips">
          <span className={`tk-chip tk-chip--status${archived ? "" : " is-done"}`}>{archived ? "Archived" : "Active"}</span>
          {item.planned_duration_minutes ? (
            <span className="tk-chip tk-chip--duration" data-testid="catalog-duration">
              Planned {formatDuration(item.planned_duration_minutes, minutesPerDay)}
            </span>
          ) : null}
        </div>
        {item.description ? (
          <section className="rec-card rec-desc">
            <div className="rec-label">
              <RecIcon name="doc" />
              Default instructions
            </div>
            <p className="rec-desc__text">{item.description}</p>
          </section>
        ) : null}
        <p className="tk-help">
          A reusable definition. Adding it to a project copies it into a separate Project Task, so changing it here
          never changes work already on a project.
        </p>
        <div className="tl-meta">
          {item.created_at ? <div>Created {formatLogTimestamp(item.created_at)}</div> : null}
          {item.updated_at ? <div>Updated {formatLogTimestamp(item.updated_at)}</div> : null}
        </div>
        <div className="pj-pagepad" aria-hidden="true" />
      </div>
      {canEdit ? (
        <div className="rec-actions">
          <button type="button" className="rec-btn rec-btn--outline" onClick={() => onEdit(item)}>
            <RecIcon name="pencil" />
            Edit
          </button>
        </div>
      ) : null}
    </div>
  );
}

function CatalogEditor({ item, onCancel, onSubmit, onRestoreExisting }) {
  const { minutesPerDay } = useWorkSchedule();
  useKeyboardOpen(true);
  useKeepFocusedFieldVisible(true);
  const [title, setTitle] = useState(item?.title || "");
  const [description, setDescription] = useState(item?.description || "");
  const [duration, setDuration] = useState(() => durationToInput(item?.planned_duration_minutes, minutesPerDay));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [existing, setExisting] = useState(null);

  async function save() {
    if (saving) return;
    const parsed = parseDurationInput(duration, minutesPerDay);
    const checked = validateTaskInput({ title, description });
    setErrors(parsed.ok ? checked.errors : { ...checked.errors, duration: parsed.error });
    setError("");
    setExisting(null);
    if (!checked.ok || !parsed.ok) return;
    setSaving(true);
    try {
      await onSubmit({ title, description, durationMinutes: parsed.minutes });
    } catch (err) {
      if (err instanceof TaskCatalogDuplicateError) {
        setErrors({ title: err.message });
        setExisting(err.existing);
      } else {
        setError(err?.message || "Unable to save the task.");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="screen nx-screen rec-page rec-page--edit" data-testid="catalog-editor">
      <RecBar title={item ? "Edit Task" : "New Task"} leading="close" onBack={onCancel} />
      <div className="rec-body">
        <section className="rec-card tk-section">
          <RecField label="Title" htmlFor="tc-title" required error={errors.title}>
            <input
              id="tc-title"
              className="rec-input"
              value={title}
              maxLength={TITLE_MAX + 20}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Install waterproof membrane"
              autoComplete="off"
            />
          </RecField>
          {existing && existing.is_active === false ? (
            <button type="button" className="tk-link" onClick={() => onRestoreExisting(existing)}>
              Restore the archived “{existing.title}”
            </button>
          ) : null}
          <RecField label="Default instructions" htmlFor="tc-desc" error={errors.description}>
            <textarea
              id="tc-desc"
              className="rec-input rec-input--area"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional. Copied onto the project task when this is added."
            />
            <div className="tk-counter">
              {description.length}/{DESCRIPTION_MAX}
            </div>
          </RecField>
          <RecField label="Planned duration" htmlFor="tc-duration" icon="clock" error={errors.duration}>
            <DurationInput id="tc-duration" amount={duration.amount} unit={duration.unit} minutesPerDay={minutesPerDay} onChange={setDuration} />
            <div className="tk-hint">Optional. Copied to project tasks made from this. Templates never carry dates.</div>
          </RecField>
        </section>
        {error ? (
          <div className="rec-banner" role="alert">
            {error}
          </div>
        ) : null}
      </div>
      <div className="rec-actions rec-actions--split">
        <button type="button" className="rec-btn rec-btn--ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="rec-btn rec-btn--primary" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

/**
 * More → Tasks: the organization's reusable work definitions (Task Catalog).
 * A catalog item is a definition, not work: no completion state here.
 */
export function TaskCatalogScreen({ onBack, initialView = "list" }) {
  const isDesktopViewport = useIsDesktopViewport();
  const { can, actor } = usePermissions();
  const canEdit = can(CAPABILITY.TASK_CATALOG_EDIT);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("active");
  const [view, setView] = useState({ type: "list" });
  const createLaunchRef = useRef(false);

  useEffect(() => {
    if (createLaunchRef.current) return;
    if (initialView === "new" && canEdit) {
      createLaunchRef.current = true;
      setView({ type: "new" });
    }
  }, [initialView, canEdit]);

  const reload = useCallback(async () => {
    try {
      setItems(await loadTaskCatalog(actor));
      setError("");
    } catch (err) {
      setError(
        isTasksUnavailableError(err)
          ? "Tasks is not set up in this environment yet."
          : err?.message || "Unable to load the Task Catalog.",
      );
    } finally {
      setLoading(false);
    }
  }, [actor]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor?.organization_id]);

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((item) => {
      if (scope === "active" && item.is_active === false) return false;
      if (scope === "archived" && item.is_active !== false) return false;
      if (needle && !`${item.title} ${item.description || ""}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [items, scope, search]);

  async function toggleActive(item) {
    if (blockDemoSave(actor)) return;
    const archiving = item.is_active !== false;
    if (archiving && !window.confirm(`Archive "${item.title}"? It stays on projects that already use it.`)) return;
    try {
      const updated = await setCatalogTaskActive(actor, item.task_catalog_id, !archiving);
      await reload();
      setView((v) => (v.item ? { ...v, item: updated } : v));
    } catch (err) {
      setError(err?.message || "Unable to update the task.");
    }
  }

  if (view.type === "details") {
    const item = items.find((i) => i.task_catalog_id === view.item.task_catalog_id) || view.item;
    return (
      <CatalogDetails
        item={item}
        canEdit={canEdit}
        onBack={() => setView({ type: "list" })}
        onEdit={(target) => setView({ type: "edit", item: target })}
        onToggleActive={toggleActive}
      />
    );
  }

  if (view.type === "edit" || view.type === "new") {
    return (
      <CatalogEditor
        item={view.type === "edit" ? view.item : null}
        onCancel={() => setView(view.type === "edit" ? { type: "details", item: view.item } : { type: "list" })}
        onRestoreExisting={async (existing) => {
          await setCatalogTaskActive(actor, existing.task_catalog_id, true);
          await reload();
          setView({ type: "list" });
        }}
        onSubmit={async ({ title, description, durationMinutes }) => {
          if (blockDemoSave(actor)) throw new Error("Demo Mode — Sign in to save your work.");
          if (view.type === "edit") {
            const updated = await updateCatalogTask(actor, view.item.task_catalog_id, { title, description, durationMinutes }, { existing: items });
            await reload();
            setView({ type: "details", item: updated });
          } else {
            await createCatalogTask(actor, { title, description, durationMinutes }, { existing: items });
            await reload();
            setView({ type: "list" });
          }
        }}
      />
    );
  }

  if (isDesktopViewport) {
    return (
      <TaskCatalogDesktop
        onBack={onBack}
        items={items}
        shown={shown}
        loading={loading}
        error={error}
        canEdit={canEdit}
        scope={scope}
        onScopeChange={setScope}
        search={search}
        onSearch={setSearch}
        onNew={() => setView({ type: "new" })}
        onOpen={(target) => setView({ type: "details", item: target })}
        onEdit={(target) => setView({ type: "edit", item: target })}
        onToggleActive={toggleActive}
      />
    );
  }

  const noun = shown.length === 1 ? "task" : "tasks";
  return (
    <CrewPilotModuleShell
      title="Tasks"
      subtitle="Reusable work definitions"
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      primary
      compact
    >
      <TimeLogsScopeTabs
        ariaLabel="Task catalog status"
        options={[
          { value: "active", label: "Active" },
          { value: "archived", label: "Archived" },
          { value: "all", label: "All" },
        ]}
        value={scope}
        onChange={setScope}
      />
      <div className="mat-searchbox">
        <RecIcon name="search" />
        <input
          type="search"
          className="mat-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tasks..."
          aria-label="Search tasks"
          autoComplete="off"
        />
      </div>
      <TimeLogsActionBar
        summary={`${shown.length} ${noun}`}
        onNew={canEdit ? () => setView({ type: "new" }) : null}
        newLabel="New Task"
        newAriaLabel="+ New Task"
      />
      {error ? (
        <div className="rec-banner" role="alert" style={{ marginBottom: 10 }}>
          {error}
        </div>
      ) : null}
      {loading ? (
        <p className="pj-empty">Loading…</p>
      ) : shown.length === 0 ? (
        <p className="pj-empty" data-testid="catalog-empty">
          {items.length === 0
            ? canEdit
              ? "No catalog tasks yet. Add the work you repeat on projects."
              : "No catalog tasks yet."
            : "No tasks match."}
        </p>
      ) : (
        <div className="tk-list">
          {shown.map((item) => (
            <CatalogRow
              key={item.task_catalog_id}
              item={item}
              canEdit={canEdit}
              onOpen={(target) => setView({ type: "details", item: target })}
              onEdit={(target) => setView({ type: "edit", item: target })}
              onToggleActive={toggleActive}
            />
          ))}
        </div>
      )}
    </CrewPilotModuleShell>
  );
}
